import React, { useState, useEffect, useCallback } from 'react';
import { kpiApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { completedMonths } from '../lib/utils';

export default function KpiTab({ employeeId, canEdit }) {
  const [sum, setSum] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [tmpl, setTmpl] = useState('');
  const [month, setMonth] = useState('');
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);
  const [showCrit, setShowCrit] = useState(true);
  // Per-person KPI editing. A role template is a starting point, not a cage —
  // this is where one person's sheet is made to differ from it.
  const [master, setMaster] = useState(null);
  const [pickOpen, setPickOpen] = useState(false);
  const [pickQ, setPickQ] = useState('');

  const openPicker = async () => {
    setPickOpen((v) => !v);
    if (master) return;
    try {
      const r = await kpiApi.master();
      setMaster((r.data?.sources || []).flatMap((g) =>
        (g.kpis || []).map((k) => ({ ...k, sourceLabel: g.label || g.source }))));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not read the KPI master'); }
  };

  const addKpi = async (k) => {
    setBusy(true);
    try {
      const r = await kpiApi.addKpiRow(employeeId, { fy: sum.fy, kpi_key: k.key });
      toast.success(`${r.data.added} added — ${r.data.count} KPIs, ${r.data.each_worth_pct}% each`);
      setPickQ('');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add'); }
    finally { setBusy(false); }
  };

  const removeKpi = async (row) => {
    setBusy(true);
    try {
      await kpiApi.removeKpiRow(employeeId, row.kpi_id, { fy: sum.fy });
      toast.success(`${row.kpi} removed`);
      await load();
    } catch (e) {
      // 409 means the row already carries marks. The server says how many, so
      // the confirmation is a real question rather than a shrug.
      const detail = e?.response?.data?.detail || 'Could not remove';
      if (e?.response?.status === 409 && window.confirm(`${detail}\n\nRemove it anyway?`)) {
        try {
          const r = await kpiApi.removeKpiRow(employeeId, row.kpi_id, { fy: sum.fy, force: true });
          toast.success(`${row.kpi} removed · ${r.data.orphaned_marks} past mark(s) kept on record`);
          await load();
        } catch (e2) { toast.error(e2?.response?.data?.detail || 'Could not remove'); }
      } else if (e?.response?.status !== 409) {
        toast.error(detail);
      }
    } finally { setBusy(false); }
  };

  // Pull the month's Operations actuals for this employee's unit and write the
  // marks onto this sheet. Previewed first so the manager sees what lands.
  const fillFromOps = async (m) => {
    if (!m || !sum) return;
    setBusy(true);
    try {
      const pre = await kpiApi.employeeFill({ employee_id: employeeId, fy: sum.fy, month: m, dry_run: true });
      const n = (pre.data.rows || []).length;
      if (!n) { toast.error(`No Operations marks available for ${m} (${pre.data.unit || 'no unit'})`); return; }
      const lines = pre.data.rows.map((r) => `${r.kpi}: ${r.actual ?? '—'} → ${r.score}`).join('\n');
      if (!window.confirm(`Write ${n} Operations mark(s) for ${m} — ${pre.data.unit}?\n\n${lines}\n\nVerified scores are never overwritten.`)) return;
      const r = await kpiApi.employeeFill({ employee_id: employeeId, fy: sum.fy, month: m, dry_run: false });
      toast.success(`${r.data.written} mark(s) written for ${m}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Fill from Operations failed'); }
    finally { setBusy(false); }
  };

  const load = useCallback(async () => {
    try {
      const [s, t] = await Promise.all([kpiApi.summary(employeeId), kpiApi.templates()]);
      setSum(s.data);
      setTemplates(t.data || []);
      const enterable = completedMonths(s.data.fy);
      setMonth(enterable.length ? enterable[enterable.length - 1] : '');
    } catch (e) { toast.error('Failed to load KPI data'); }
  }, [employeeId]);

  useEffect(() => { load(); }, [load]);

  const applyTemplate = async () => {
    if (!tmpl) { toast.error('Pick a template'); return; }
    setBusy(true);
    try {
      const r = await kpiApi.setDefinitions(employeeId, { fy: sum.fy, template_name: tmpl });
      toast.success(`Applied ${r.data.count} KPIs (weight ${r.data.weight_total}%)`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Apply failed'); }
    finally { setBusy(false); }
  };

  const seedDefaults = async () => {
    setBusy(true);
    try { const r = await kpiApi.seedTemplates(); toast.success(`Templates ready (${r.data.created} added)`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Seed failed'); }
    finally { setBusy(false); }
  };

  const toggleLock = async () => {
    setBusy(true);
    try { await kpiApi.lockDefinitions(employeeId, sum.fy, !sum.locked); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Lock failed'); }
    finally { setBusy(false); }
  };

  const saveScores = async () => {
    if (!month) return;
    setBusy(true);
    try {
      // Touched boxes win; untouched boxes showing an auto-prefilled mark (no
      // final/self score yet) are saved too, so "Save Aug" persists what's shown.
      const entries = [];
      for (const row of (sum.grid || [])) {
        const k = `${month}|${row.kpi_id}`;
        const c = (row.cells || {})[month] || {};
        let v = edits[k];
        if (v === undefined && c.final == null && c.self == null && c.auto_marks != null) v = c.auto_marks;
        if (v !== undefined && v !== '') entries.push([k, v]);
      }
      if (entries.length === 0) { toast.error('No changes to save'); setBusy(false); return; }
      for (const [k, v] of entries) {
        const kpi_id = k.split('|')[1];
        await kpiApi.setScore(employeeId, { fy: sum.fy, month, kpi_id, final_score: Number(v) || 0 });
      }
      toast.success(`Saved ${entries.length} score(s) for ${month}`);
      setEdits((e) => { const n = { ...e }; entries.forEach(([k]) => delete n[k]); return n; });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  if (!sum) return <div className="bg-white border border-[#E6E9F0] rounded-xl p-6 text-[#98A1B2]">Loading…</div>;

  const grid = sum.grid || [];
  const months = sum.months || [];
  // No "weights don't total 100" state here any more. The engine derives the
  // share from the KPI count (`normalised_kpi_weights`) and never reads the
  // stored weight, so a sheet with any KPIs on it always totals 100 — the old
  // warning pointed at a number that could not change a mark.
  const eachWorth = grid.length ? 100 / grid.length : 0;

  // No KPIs defined yet
  if (grid.length === 0) {
    return (
      <div className="bg-white border border-[#E6E9F0] rounded-xl p-6">
        <h2 className="font-display text-xl font-bold tracking-[-0.02em] text-[#172554] mb-2">KPI — {sum.fy}</h2>
        <p className="text-sm text-[#5A6377] mb-4">No KPIs are set for this employee yet.</p>
        {canEdit ? (
          templates.length === 0 ? (
            <div>
              <p className="text-sm text-[#5A6377] mb-2">No role templates exist. Seed the default AAMPL templates first.</p>
              <Button onClick={seedDefaults} disabled={busy}>Seed default templates</Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <select value={tmpl} onChange={(e) => setTmpl(e.target.value)}
                className="border border-[#E6E9F0] rounded-lg px-3 py-2 text-sm">
                <option value="">Choose a role template…</option>
                {templates.map((t) => <option key={t.name} value={t.name}>{t.name} ({(t.items || []).length} KPIs)</option>)}
              </select>
              <Button onClick={applyTemplate} disabled={busy}>Apply template</Button>
            </div>
          )
        ) : <p className="text-sm text-[#98A1B2]">Ask HR to assign KPIs.</p>}
      </div>
    );
  }

  const cellFor = (row, m) => (row.cells || {})[m];

  // Completed vs still-open manual rows for the month being edited. Automatic
  // rows are never "pending" — nobody has to do anything about them.
  const monthStats = (m) => {
    let done = 0, open = 0;
    grid.forEach((r) => {
      if (r.mode === 'auto') return;
      const c = (r.cells || {})[m] || {};
      (c.final != null ? done++ : open++);
    });
    return { done, open };
  };

  const goToEmployee = (id) => { if (id) window.location.href = `/employees/${id}?tab=kpi`; };
  const peerIdx = (sum.peers || []).findIndex((p) => p.id === employeeId);

  // Save, then jump straight to the next person who still owes a manual mark.
  const saveAndNext = async (m) => {
    await saveScores();
    try {
      const r = await kpiApi.nextPending({ month: m, fy: sum.fy, after: employeeId });
      const nxt = r.data.next;
      if (!nxt) { toast.success('All required KPI entries are complete.'); return; }
      toast.success(`Next: ${nxt.name} (${nxt.open} pending)`);
      goToEmployee(nxt.id);
    } catch (e) { /* saving already succeeded; navigation is a convenience */ }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white border border-[#E6E9F0] rounded-xl p-6">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h2 className="font-display text-xl font-bold tracking-[-0.02em] text-[#172554]">KPI — {sum.fy}</h2>
            {sum.employee && (
              <p className="text-[11px] text-[#98A1B2]">
                {[sum.employee.department, sum.employee.designation, sum.employee.unit]
                  .filter(Boolean).join(' · ')}
              </p>
            )}
            <p className="text-xs text-[#5A6377] mt-1">
              {grid.length} KPIs
              {/* How this sheet gets its numbers — the same A/M/? vocabulary the
                  Templates list and the dashboard use. */}
              {sum.modes && (
                <>
                  {' '}(
                  {sum.modes.auto > 0 && <span className="text-[#2563EB] font-semibold">{sum.modes.auto} automatic</span>}
                  {sum.modes.auto > 0 && sum.modes.manual > 0 && ', '}
                  {sum.modes.manual > 0 && <span className="text-[#8A5A12] font-semibold">{sum.modes.manual} manual</span>}
                  {sum.modes.unmapped > 0 && <span className="text-[#C2151F] font-semibold">, {sum.modes.unmapped} not linked</span>}
                  )
                </>
              )}
              {' '}· each worth{' '}
              <span className="text-[#15A36E] font-semibold">{eachWorth.toFixed(1)}%</span>
              {sum.sheet && <span className="ml-2 text-[#98A1B2]">sheet: {sum.sheet}</span>}
              {sum.locked && <span className="ml-2 px-2 py-0.5 rounded-full bg-[#EFF1F6] text-[#5A6377] text-[10px]">Locked</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {sum.annual != null && <span className="px-3 py-1 rounded-full bg-[#E8EFFD] text-[#2563EB] text-sm font-semibold">Annual {sum.annual}%</span>}
            {sum.pending > 0 && <span className="px-2 py-1 rounded-full bg-[#FBF7EC] text-[#8A5A12] text-xs font-medium">{sum.pending} pending</span>}
            {/* Flick through people in the same role without going back to
                Allocation each time. Viewing only — never changes a template. */}
            {(sum.peers || []).length > 1 && (
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" disabled={peerIdx <= 0}
                  data-testid="kpi-prev-employee"
                  onClick={() => goToEmployee(sum.peers[peerIdx - 1]?.id)} title="Previous in this role">←</Button>
                <select value={employeeId} data-testid="kpi-employee-switcher"
                  onChange={(e) => goToEmployee(e.target.value)}
                  className="border border-[#E6E9F0] rounded-lg px-2 py-1.5 text-xs max-w-[190px]">
                  {sum.peers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <Button variant="outline" size="sm" disabled={peerIdx < 0 || peerIdx >= sum.peers.length - 1}
                  data-testid="kpi-next-employee"
                  onClick={() => goToEmployee(sum.peers[peerIdx + 1]?.id)} title="Next in this role">→</Button>
              </div>
            )}
            {canEdit && <Button variant="outline" size="sm" onClick={toggleLock} disabled={busy}>{sum.locked ? 'Unlock' : 'Lock year'}</Button>}
          </div>
        </div>
      </div>

      {/* ---- This person's KPIs ----
          REMOVED at the owner's instruction. This panel was the only
          place a KPI could be added to or taken off ONE person's sheet:
          `kpiApi.addKpiRow` / `removeKpiRow` were called from here and
          nowhere else in the app. Templates still decide what a role is
          measured on; the per-person exception no longer has a screen.
          The endpoints are untouched, so restoring this is a UI change
          only. */}

      {/* Manager score entry */}
      {canEdit && (() => {
        const enterable = completedMonths(sum.fy);
        return (
        <div className="bg-white border border-[#E6E9F0] rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <h3 className="text-sm font-semibold text-[#11182B]">Enter / verify scores</h3>
            {enterable.length > 0 ? (
              <>
                <select value={enterable.includes(month) ? month : enterable[enterable.length - 1]} onChange={(e) => setMonth(e.target.value)}
                  className="border border-[#E6E9F0] rounded-lg px-3 py-1.5 text-sm">
                  {enterable.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                {/* The month picker is the whole bar now: choose Sep and the
                    rows below show September. "Save & next pending", the
                    done/pending tally, the Operations pull and Save itself
                    have all been removed at the owner's instruction.

                    This section is therefore READ-ONLY. `kpiApi.setScore` was
                    called from here and nowhere else in the app, so with Save
                    gone there is no way to record a manual mark — and the
                    marks below are rendered as text rather than as input
                    boxes, because a box with no Save accepts a number and
                    throws it away the moment the page moves. */}
              </>
            ) : (
              <span className="text-xs text-[#8A5A12]">No completed months yet this financial year — scoring opens once a month is over.</span>
            )}
            <label className="ml-auto flex items-center gap-1.5 text-xs text-[#5A6377] cursor-pointer">
              <input type="checkbox" checked={showCrit} onChange={(e) => setShowCrit(e.target.checked)} /> Show criteria
            </label>
          </div>
          {enterable.length > 0 && (
            <div className="space-y-1">
              {grid.map((row) => {
                const c = cellFor(row, month) || {};
                const k = `${month}|${row.kpi_id}`;
                const val = edits[k] ?? (c.final ?? c.self ?? c.auto_marks ?? '');
                const isAuto = row.mode === 'auto';
                return (
                  <div key={row.kpi_id} className={`py-2 border-b border-[#F2F4F8] last:border-0 ${
                    row.mode === 'unmapped' ? 'bg-[#FFF7F7] -mx-2 px-2 rounded' : ''}`}>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="flex-1 text-[#11182B]">
                        {row.sno}. {row.kpi} <span className="text-[#98A1B2]">({eachWorth.toFixed(1)}%)</span>
                        {/* Deleted from the master but KEPT here, because the
                            months it was marked in are already scored and
                            pulling the row out would re-weight them. It reads
                            as what it is rather than as a live KPI. */}
                        {row.retired && (
                          <span className="ml-1.5 text-[10px] font-semibold text-[#5A6377] bg-[#EEF1F6] rounded px-1.5 py-0.5"
                            title={row.retired_at
                              ? `This KPI was deleted on ${new Date(row.retired_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}. Its past marks are kept so the percentages already calculated do not change. It is not collected any more.`
                              : 'This KPI was deleted. Its past marks are kept so the percentages already calculated do not change.'}>
                            KPI DELETED
                          </span>
                        )}
                        {isAuto && !row.retired && <span className="ml-1.5 text-[10px] font-semibold text-[#2563EB] bg-[#E8EFFD] rounded px-1.5 py-0.5">AUTO</span>}
                        {row.mode === 'unmapped' && (
                          <span className="ml-1.5 text-[10px] font-semibold text-[#C2151F] bg-[#FDECEC] rounded px-1.5 py-0.5"
                            title="This row is not linked to any KPI in the master, so it can never be filled automatically">
                            NOT LINKED
                          </span>
                        )}
                      </span>
                      {c.auto_marks == null && c.auto_actual && (
                        <span className="text-[11px] text-[#98A1B2]" title="Actual from Operations — marks pending target/band">{c.auto_actual}</span>
                      )}
                      {c.self != null && !isAuto && <span className="text-[11px] text-[#8A5A12]">self {c.self}</span>}
                      {/* An automatic KPI is never typed: the software's number IS
                          the answer, so it is shown rather than offered as an
                          editable box that would quietly compete with it. */}
                      {isAuto ? (
                        <span className="w-20 text-right text-sm font-semibold text-[#2563EB]"
                          title={c.auto_actual ? `actual ${c.auto_actual}` : 'filled by the software'}>
                          {c.final ?? c.auto_marks ?? '—'}
                        </span>
                      ) : (
                        /* Shown, not offered. There is no Save on this screen
                           any more, so an editable box could only mislead. An
                           em dash where a mark is missing says "nothing
                           recorded" — a 0 would be a mark nobody gave. */
                        <span className="w-20 text-right text-sm font-semibold text-[#11182B]"
                          title="Manual KPI — no mark recorded for this month yet">
                          {val === '' || val == null ? '—' : val}
                        </span>
                      )}
                    </div>
                    {showCrit && (row.objective || row.criteria) && (
                      <div className="mt-1 ml-4 text-[11px] text-[#5A6377] bg-[#F7F8FB] border border-[#EEF1F6] rounded-md px-2 py-1">
                        {row.objective && <div><span className="font-semibold text-[#11182B]">Target:</span> {row.objective}</div>}
                        {row.criteria && <div><span className="font-semibold text-[#11182B]">Scoring:</span> {row.criteria}</div>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        );
      })()}

      {/* Year matrix */}
      <div className="bg-white border border-[#E6E9F0] rounded-xl p-6 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-[#5A6377]">
            <tr className="bg-[#EFF1F6]">
              <th className="py-2 px-2 text-left sticky left-0 bg-[#EFF1F6]">KPI</th>
              <th className="py-2 px-2 text-right">Wt%</th>
              {months.map((m) => <th key={m} className="py-2 px-2 text-center">{m}</th>)}
            </tr>
          </thead>
          <tbody>
            {grid.map((row) => (
              <tr key={row.kpi_id} className="border-b border-[#F2F4F8]">
                <td className="py-1.5 px-2 text-[#11182B] sticky left-0 bg-white">
                  {row.sno}. {row.kpi}
                  {row.retired && (
                    <span className="ml-1.5 text-[10px] font-semibold text-[#5A6377] bg-[#EEF1F6] rounded px-1.5 py-0.5">
                      deleted
                    </span>
                  )}
                </td>
                <td className="py-1.5 px-2 text-right text-[#5A6377]">{row.weight}</td>
                {months.map((m) => {
                  const c = (row.cells || {})[m];
                  if (!c) return <td key={m} className="py-1.5 px-2 text-center text-[#CBD2E0]">–</td>;
                  const pend = c.final == null;
                  return <td key={m} className={`py-1.5 px-2 text-center ${pend ? 'text-[#8A5A12]' : 'text-[#11182B]'}`}>
                    {c.final != null ? c.final
                      : (c.self != null ? `(${c.self})`
                        : (c.auto_marks != null
                          ? <span className="text-[#2563EB]" title={c.auto_actual || 'auto'}>{c.auto_marks}*</span>
                          : '–'))}
                  </td>;
                })}
              </tr>
            ))}
            <tr className="bg-[#F7F8FB] font-semibold">
              <td className="py-2 px-2 text-[#11182B] sticky left-0 bg-[#F7F8FB]">Weighted score %</td>
              <td className="py-2 px-2"></td>
              {months.map((m) => <td key={m} className="py-2 px-2 text-center text-[#2563EB]">{sum.monthly?.[m] != null ? sum.monthly[m] : '–'}</td>)}
            </tr>
          </tbody>
        </table>
        <p className="text-[11px] text-[#98A1B2] mt-2">Self-scores show in (brackets) until the manager verifies. Blue values with * are auto-computed (hover for basis) and become final on Save. Weighted % = Σ(score ÷ 4 × weight) over scored KPIs. Annual = average of scored months.</p>
      </div>
    </div>
  );
}
